import { defineStore } from 'pinia';
import { useAdvisorStore } from './advisorStore.js';

export const useTaskStore = defineStore('task', {
  state: () => ({
    csrfToken: null,
    recommendations: {
      isFetching: false,
      hasFetched: false,
      items: [],
      entities: [],
      advisors: [],
      processedItems: [],
      filters: {
        advisors: [],
        entities: [],
        type: [],
      },
    },
    tasks: {
      isFetching: false,
      isSavingTask: false,
      hasFetched: false,
      items: [],
      allAdvisors: [],
      advisorsWithTasks: [],
      allEntities: [],
      entitiesWithTasks: [],
      advisors: [],
      filters: {
        search: '',
        status: '',
        assignees: [],
        entities: [],
        priorities: [],
        dueDateBefore: '',
        dueDateAfter: '',
        overdue: false,
      },
    },
  }),

  getters: {
    headers() {
      return {
        'X-CSRFToken': this.csrfToken,
        'Content-Type': 'application/json',
      };
    },
  },

  actions: {
    setCSRF(token) {
      this.csrfToken = token;
    },
    getActionByKey(key) {
      const map = {
        tasks: this.fetchTasks,
        recommendations: this.fetchRecommendations,
      };
      return map[key] || (() => console.warn(`No fetch action for ${key}`));
    },
    setFetchingTasks(newValue) {
      this.tasks.isFetching = newValue;
    },
    setSavingTask(newValue) {
      this.tasks.isSavingTask = newValue;
    },
    setTasks(data) {
      // this.tasks.allAdvisors = all_advisors;
      // this.tasks.advisorsWithTasks = advisors_with_tasks;
      // this.tasks.allEntities = all_entities;
      // this.tasks.entitiesWithTasks = entities_with_tasks;
      this.tasks.items = data;
      this.tasks.hasFetched = true;
    },
    addToProcessRecommendations(task) {
      if (!this.recommendations.processedItems.includes(task.external_id)) {
        this.recommendations.processedItems.push(task.external_id);
      }
    },
    resetFilterArray(resource, key) {
      if (Array.isArray(this[resource].filters[key])) {
        this[resource].filters[key] = [];

        const fetchFn = this.getActionByKey(resource);
        fetchFn(); // Call the correct fetch action dynamically
      }
    },
    hasFilterValue(resource, key, value) {
      return (
        Array.isArray(this[resource].filters[key]) &&
        this[resource].filters[key].includes(value)
      );
    },
    toggleFilterValue(resource, key, value) {
      const filterArray = this[resource].filters[key];
      if (!Array.isArray(filterArray)) return;

      const index = filterArray.indexOf(value);
      if (index === -1) {
        filterArray.push(value);
      } else {
        filterArray.splice(index, 1);
      }
    },
    removeFilterValue(resource, key, value) {
      console.log('Removing filter value:', { resource, key, value });
      console.log(this[resource]);
      const current = this[resource]?.filters?.[key];

      if (Array.isArray(current)) {
        this[resource].filters[key] = current.filter(item => {
          if (
            typeof item === 'object' &&
            item !== null &&
            typeof value === 'object' &&
            value !== null
          ) {
            return item.id !== value.id;
          }
          return item !== value;
        });
      } else if (typeof current === 'string') {
        this[resource].filters[key] = '';
      } else if (typeof current === 'boolean') {
        this[resource].filters[key] = false;
      }

      const fetchFn = this.getActionByKey(resource);
      fetchFn();
    },
    setSearch(searchTerm) {
      this.tasks.filters.search = searchTerm;
    },
    setStatus(status) {
      this.tasks.filters.status = status;
    },
    resetTaskFilters() {
      Object.assign(this.tasks.filters, {
        search: '',
        assignees: [],
        entities: [],
        priorities: [],
        dueDateBefore: '',
        dueDateAfter: '',
        overdue: false,
      });
      this.fetchTasks();
    },
    async fetchTasks() {
      this.setFetchingTasks(true);

      const {
        entities,
        search,
        status,
        assignees,
        priorities,
        dueDateBefore,
        dueDateAfter,
        overdue,
      } = this.tasks.filters;
      const searchParams = new URLSearchParams();

      if (search) searchParams.append('search', search);

      if (status && status.length > 0) {
        // support string or array
        const statusValue = Array.isArray(status) ? status.join(',') : status;
        searchParams.append('status', statusValue);
      }

      if (priorities && priorities.length > 0) {
        searchParams.append('priorities', priorities.join(','));
      }

      if (assignees && assignees.length > 0) {
        const ids = assignees.map(a => (typeof a === 'object' ? a.id : a));
        searchParams.append('assignees', ids.join(','));
      }

      if (entities && entities.length > 0) {
        const externalIds = entities.map(e =>
          typeof e === 'object' ? e.external_id : e,
        );
        searchParams.append('entities', externalIds.join(','));
      }

      if (dueDateBefore) {
        searchParams.append('due_date_before', dueDateBefore);
      }

      if (dueDateAfter) {
        searchParams.append('due_date_after', dueDateAfter);
      }

      if (overdue === true) {
        searchParams.append('overdue', 'true');
      }

      const params = searchParams.toString() ?
        `?${searchParams.toString()}` :
        '';

      try {
        const response = await fetch(`/api/v1/action-center/tasks/${params}`);
        const data = await response.json();
        this.setTasks(data);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        this.setFetchingTasks(false);
      }
    },
    async saveTask(task, refetch = false) {
      const advisorStore = useAdvisorStore();
      const id = task.externalId || task.id;

      this.setSavingTask(true);

      try {
        const response = await fetch(`/api/v1/action-center/tasks/${id}/`, {
          method: 'PATCH',
          body: JSON.stringify({
            name: task.name,
            description: task.description,
            priority: task.priority,
            due_date: task.dueDate,
            entity: task.entityExternalId,
            assignees: task.assigneeIds,
            status: task.status,
            snooze_until: task.snooze,
          }),
          headers: advisorStore.headers,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(JSON.stringify(errorData));
        }

        const updatedTask = await response.json();

        // Keep track of updated tasks so we can hide them in the UI
        if (updatedTask.status) {
          this.addToProcessRecommendations(updatedTask);
        }

        if (refetch) {
          this.fetchTasks();
        } else {
          // Optional: replace the task in your store's items list
          const index = this.tasks.items.findIndex(t => t.id === task.id);

          if (index !== -1) {
            this.tasks.items.splice(index, 1, updatedTask);
          }
        }

        return updatedTask;
      } catch (error) {
        console.error('Error updating task:', error);
        throw error;
      } finally {
        this.setSavingTask(false);
      }
    },
    setFetchingRecommendations(newValue) {
      this.recommendations.isFetching = newValue;
    },
    setRecommendations(data) {
      // const { entities, items } = data;
      // const flattened = data.flatMap(entry => {
      //   const entity = entry.entity;
      //   const entityInfo = {
      //     entity_id: entity.id,
      //     entity_name: entity.name,
      //     entity_external_id: entity.external_id,
      //   };

      //   return (entry.recommendations_data?.basic_recommendations || []).map(
      //       (rec, index) => ({
      //         id: `${entry.id}-${index}`,
      //         headline: rec.headline,
      //         action: rec.action,
      //         detail: rec.detail,
      //         ...entityInfo,
      //       }),
      //   );
      // });

      const recs = data.map(rec => ({
        id: rec.external_id,
        entity_id: rec.entity?.id,
        entity_name: rec.entity?.name,
        entity_external_id: rec.entity?.external_id,
        headline: rec.name,
        action: rec.description,
        detail: rec.notes,
      }));

      this.recommendations.items = recs;
      // this.recommendations.entities = entities;
      this.recommendations.hasFetched = true;
    },
    async fetchRecommendations() {
      this.setFetchingRecommendations(true);

      const { entities } = this.recommendations.filters;
      const searchParams = new URLSearchParams();

      searchParams.append('status', 'none');

      if (entities.length > 0) {
        searchParams.append('entities', entities.join(','));
      }

      const params = searchParams.toString() ?
        `?${searchParams.toString()}` :
        '';

      try {
        const response = await fetch(`/api/v1/action-center/tasks/${params}`);
        const data = await response.json();
        this.setRecommendations(data);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
      } finally {
        this.setFetchingRecommendations(false);
      }
    },
  },
});
